import React, { FC, Suspense } from 'react'
import classNames from 'classnames'
import { ImageCardProps } from './types.d'
import Typography from '@toasttab/shared/src/components/atoms/Typography/Typography'
import TextLink from '@toasttab/shared/src/components/molecules/TextLink/TextLink'
import { Card, Text } from '@toasttab/shared/src/utils/craftUtils'
import VideoAnimation from '@toasttab/shared/src/components/atoms/VideoAnimation/VideoAnimation'
import VidyardVideoPlayer from '@toasttab/shared/src/components/molecules/VidyardVideo/VidyardVideoPlayer'
import VidyardPlayButton from '@toasttab/shared/src/components/molecules/VidyardPlayButton/VidyardPlayButton'
import Image from '@toasttab/shared/src/components/atoms/Image/Image'
import Badge from '@toasttab/shared/src/components/atoms/Badge/Badge'
import { badgeColorType } from '@toasttab/shared/src/components/atoms/Badge/Badge.d'
import { LottieProps } from '@toasttab/shared/src/components/atoms/LottiePlayer/LottiePlayer.d'
const LottiePlayer: FC<LottieProps> = React.lazy(
  () =>
    import('@toasttab/shared/src/components/atoms/LottiePlayer/LottiePlayer')
)

const ImageCard = ({
  badge,
  badgeColor,
  bgColor,
  cardBorderColor,
  ctaLabel,
  ctaLink,
  dataTestId = 'ImageCard-1',
  elevation,
  featuredImage,
  headingBadge,
  image,
  imageRound,
  video,
  linkIcons,
  loopAnimation,
  padding,
  sectionDescription,
  sectionHeading,
  slideIsActive,
  textAlignment,
  fourCardLayout = false
}: ImageCardProps) => {
  const renderContentImage = () => {
    const imageRoundClass = imageRound ? 'rounded-lg' : 'rounded-t-lg'
    const imgClasses = classNames(
      imageRoundClass,
      'max-h-[220px] w-auto mx-auto',
      {
        'lg:h-[220px]': !fourCardLayout,
        'lg:h-[161px] lg:max-h-[161px]': fourCardLayout
      }
    )
    switch (image[0].extension) {
      case 'json':
        return (
          <Suspense fallback={null}>
            <LottiePlayer
              classes={imageRoundClass}
              corners={imageRound}
              direction={1}
              hover
              lottieFile={image[0].url}
              previewImage={featuredImage}
            />
          </Suspense>
        )
      case 'mp4':
      case 'webm':
        return (
          <div className={`${imageRoundClass} w-full relative overflow-hidden`}>
            <VideoAnimation
              animation={image[0]}
              autoPlay
              classes={`${imageRoundClass} w-full h-full object-cover`}
              corners
              loop={loopAnimation}
              previewImage={featuredImage}
            />
          </div>
        )
      default:
        return (
          <img
            alt=''
            className={imgClasses}
            src={`${
              image[0].optimizedImages
                ? image[0].optimizedImages.srcAvif
                : image[0].url
            }`}
          />
        )
    }
  }

  const renderImageVideo = () => {
    return (
      <div className='col-span-4 md:col-span-6 relative [&>div:nth-child(1)]:z-10 [&>div:nth-child(2)]:z-0'>
        {renderVideo(video.vidyardId, tabIndex, video.videoButtonClasses)}
        {renderVideoImage(video.image)}
      </div>
    )
  }

  const renderVideo = (
    vidyardId: string,
    tabIndex: number,
    videoButtonClasses: string
  ) => {
    if (!vidyardId) return
    const videoButtonCss = classNames(
      'bg-white inline-flex items-center justify-center h-[56px] py-5 px-7 m-auto',
      videoButtonClasses
    )
    return (
      <div
        className='flex absolute top-0 bottom-0 left-0 right-0'
        data-testid='ImageCard-Video'
      >
        <VidyardPlayButton
          classes={videoButtonCss}
          tabIndex={tabIndex}
          variation='secondary'
          onClick={async () => {
            if (typeof window !== 'undefined') {
              // @ts-ignore - Typescript defs missing for @vidyard/embed-code
              const vidyardEmbed = await import('@vidyard/embed-code')
              if (vidyardEmbed.api) {
                const players = vidyardEmbed.api.getPlayersByUUID(vidyardId)
                if (players && players.length > 0) {
                  players[0].showLightbox()
                }
              }
            }
          }}
        />
        <div className='hidden'>
          <VidyardVideoPlayer vidyardId={vidyardId} />
        </div>
      </div>
    )
  }

  const renderVideoImage = (image: any) => {
    if (!image) {
      return
    }
    const imgClasses = classNames(
      image.classes,
      'w-full h-full max-h-[220px] w-auto mx-auto',
      {
        'rounded-lg lg:rounded-md': imageRound,
        'rounded-t-lg lg:rounded-t-md': !imageRound,
        'lg:h-[220px]': !fourCardLayout,
        'lg:h-[161px] lg:max-h-[161px]': fourCardLayout
      }
    )
    return <Image {...image} classes={imgClasses} />
  }

  const wrapperClasses = classNames(
    'rounded-lg',
    Card.backgroundColor(bgColor),
    Card.borderColor(cardBorderColor),
    Card.elevation(elevation),
    {
      border: Card.borderColor(cardBorderColor) !== '',
      'hover:shadow-3xl':
        ctaLink && ctaLabel && Card.borderColor(cardBorderColor) !== ''
    }
  )
  let tabIndex = 0
  if (slideIsActive) {
    tabIndex = slideIsActive ? 0 : -1
  }
  let badgeBgColor = ''
  switch (badgeColor) {
    case 'Blue':
      badgeBgColor = 'bg-sky-0'
      break
    case 'Green':
      badgeBgColor = 'bg-grass-25'
      break
    case 'Yellow':
      badgeBgColor = 'bg-yellow-25'
      break
    default:
      badgeBgColor = 'bg-sky-0'
  }

  return (
    <div className={wrapperClasses} data-testid={dataTestId}>
      {image ? renderContentImage() : null}
      {video ? renderImageVideo() : null}
      <div
        data-testid={`${dataTestId}-content`}
        className={`${Text.alignment(textAlignment)} ${Card.cardPadding(
          padding
        )}`}
      >
        {badge && headingBadge ? (
          <Badge
            badgeColor={badgeBgColor as badgeColorType}
            classes='mb-4'
            text={badge}
            uppercase={true}
          />
        ) : null}
        <Typography
          as='h4'
          classes='text-default mb-4 !font-normal'
          variation='h4'
        >
          {sectionHeading}
        </Typography>
        <Typography as='p' classes='text-gray-100 mb-4' variation='default'>
          {sectionDescription}
        </Typography>
        {badge && !headingBadge ? (
          <div className='mb-3'>
            <Badge
              badgeColor={badgeBgColor as badgeColorType}
              text={badge}
              uppercase={true}
            />
          </div>
        ) : null}
        {ctaLink && ctaLabel ? (
          <TextLink
            classes={linkIcons}
            description={`${sectionHeading} ${ctaLabel} `}
            href={ctaLink}
            icon={linkIcons ? linkIcons : 'none'}
            tabIndex={tabIndex}
            text={ctaLabel}
          />
        ) : null}
      </div>
    </div>
  )
}

export default ImageCard
